import React from 'react';
import { industries, benefits } from '../data/mock';
import { CheckCircle } from 'lucide-react';

const IndustriesSection = () => {
  return (
    <>
      {/* Industries We Serve */}
      <section id="industries" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Industries We Serve</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
                Trademark Protection Across All Industries
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                We provide specialized trademark registration services to businesses across diverse industries, protecting brands of all sizes and sectors.
              </p>
            </div>

            {/* Industries Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="bg-accent border-2 border-accent rounded-lg p-6 text-center hover:border-secondary hover:shadow-lg transition-all"
                >
                  <div className="text-secondary mb-2">
                    <CheckCircle size={24} className="mx-auto" />
                  </div>
                  <div className="text-primary font-semibold text-sm">{industry}</div>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="bg-primary text-white rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">Your Industry Not Listed?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                We serve businesses across all sectors. No matter your industry, our trademark experts can help protect your brand with customized registration strategies.
              </p>
              <button className="bg-secondary text-white px-8 py-3 rounded-md hover:bg-secondary/90 transition-all font-semibold">
                Contact Us Today
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Key Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
                Benefits of Federal Trademark Registration
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Registering your trademark with the USPTO provides powerful legal protections and business advantages that common law rights alone cannot offer.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-all border-2 border-transparent hover:border-secondary flex flex-col h-full min-h-[280px]"
                >
                  <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4 flex-shrink-0">
                    <CheckCircle className="text-secondary" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3 flex-shrink-0">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm flex-1">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Additional Value Proposition */}
            <div className="mt-16 bg-white rounded-lg p-8 md:p-12 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-primary mb-4">
                    Why Federal Registration Matters
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    While common law trademark rights exist through use, federal registration with the USPTO provides significantly stronger legal protection. It establishes a public record of your claim, creates a legal presumption of ownership nationwide, and gives you the exclusive right to use the mark on or in connection with the goods/services listed in the registration.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Federal registration also allows you to bring trademark infringement lawsuits in federal court, potentially recover treble damages and attorney fees, and use the ® symbol to indicate registration. These powerful benefits make federal trademark registration essential for serious brand protection.
                  </p>
                </div>
                <div className="bg-accent/30 rounded-lg p-8">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="font-semibold text-primary">Nationwide Priority</div>
                        <div className="text-sm text-gray-600">Your filing date establishes priority across all 50 states</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="font-semibold text-primary">Legal Presumption</div>
                        <div className="text-sm text-gray-600">Registration creates presumption of ownership and validity</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="font-semibold text-primary">Customs Protection</div>
                        <div className="text-sm text-gray-600">Stop counterfeit imports at U.S. borders</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="font-semibold text-primary">Domain Name Recovery</div>
                        <div className="text-sm text-gray-600">Basis for claiming domain names and social media handles</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustriesSection;
